import type { ImportResult } from '@/api/types';

interface MultipartPart {
  name: string;
  contentType?: string;
  body: string;
}

/**
 * Parse a multipart/form-data response into its constituent parts
 */
const parseMultipartResponse = (rawText: string, boundary: string): MultipartPart[] => {
  const parts: MultipartPart[] = [];

  // Split by boundary, filter out empty parts and closing boundary (--)
  const rawParts = rawText
    .split(`--${boundary}`)
    .filter(part => part.trim() && !part.trim().startsWith('--'));

  for (const rawPart of rawParts) {
    // Normalize line endings to \n for consistent parsing
    const normalizedPart = rawPart.replace(/\r\n/g, '\n');

    // Find the blank line that separates headers from body
    const headerBodySeparator = normalizedPart.indexOf('\n\n');
    if (headerBodySeparator === -1) {
      continue;
    }

    const headerSection = normalizedPart.substring(0, headerBodySeparator);
    const body = normalizedPart.substring(headerBodySeparator + 2).trim();

    // Parse headers into key-value pairs
    const headers = headerSection.split('\n').reduce(
      (acc, line) => {
        const colonIndex = line.indexOf(':');
        if (colonIndex !== -1) {
          const key = line.substring(0, colonIndex).trim().toLowerCase();
          const value = line.substring(colonIndex + 1).trim();
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );

    // Extract name from Content-Disposition header
    const contentDisposition = headers['content-disposition'] || '';
    const nameMatch = contentDisposition.match(/name="([^"]+)"/);
    if (!nameMatch) {
      continue;
    }

    parts.push({ name: nameMatch[1], contentType: headers['content-type'], body });
  }

  return parts;
};

export const extractCsvResponse = async (
  response: Response,
): Promise<{ importResult: ImportResult; errorReport?: Blob }> => {
  const contentType = response.headers.get('content-type');
  if (!contentType) {
    throw new Error('Missing content-type');
  }

  // Extract boundary using regex to handle edge cases (e.g., boundary with quotes)
  const boundaryMatch = contentType.match(/boundary=([^;]+)/);
  if (!boundaryMatch) {
    throw new Error('Missing boundary in content-type');
  }
  const boundary = boundaryMatch[1].trim().replace(/^["']|["']$/g, '');

  const rawText = await response.text();
  console.log('[extractCsvResponse] rawText: ', rawText);
  const parts = parseMultipartResponse(rawText, boundary);

  console.log('[extractCsvResponse] parts: ', parts);

  let importResult: ImportResult | undefined;
  let errorReport: Blob | undefined;

  for (const part of parts) {
    if (part.name === 'importResult') {
      importResult = JSON.parse(part.body);
    } else if (part.name === 'errorReport') {
      errorReport = new Blob([part.body], { type: part.contentType || 'text/csv' });
    }
  }

  console.log('[extractCsvResponse] importResult: ', importResult);

  if (!importResult) {
    throw new Error('importResult not found in response');
  }

  return { importResult, errorReport };
};
